import type {
  NavyCostTimeTier,
  RenderNavyCostTimeReportToolOutput,
} from '@azymmetric/shared/tools/render-navy-cost-time-report.tool-config';
import { cn } from '@lib/utils';
import type { FC, ReactNode } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Scatter,
  ScatterChart,
  Tooltip,
  XAxis,
  YAxis,
  ZAxis,
} from 'recharts';

type NavyCostTimeReportArtifactProps = {
  report: RenderNavyCostTimeReportToolOutput;
};

type ChartSectionProps = {
  children: ReactNode;
  className?: string;
  description?: string;
  title: string;
};

const TIER_ORDER: NavyCostTimeTier[] = ['Field', 'Pilot', 'Watch', 'Pass'];

const TIER_COLORS: Record<NavyCostTimeTier, string> = {
  Field: '#1f8a4c',
  Pass: '#9aa3b2',
  Pilot: '#6f42c1',
  Watch: '#d97706',
};

type ActivityKey =
  | 'compliance'
  | 'concept'
  | 'demonstration'
  | 'engineering'
  | 'production'
  | 'riskReduction'
  | 'verification';

const ACTIVITY_KEYS = [
  { color: '#0ea5e9', key: 'concept', label: 'Concept' },
  { color: '#1d4ed8', key: 'engineering', label: 'Engineering' },
  { color: '#0f8a8d', key: 'verification', label: 'V&V' },
  { color: '#b42318', key: 'riskReduction', label: 'Risk Reduction' },
  { color: '#6f42c1', key: 'compliance', label: 'Compliance/ATO' },
  { color: '#1f8a4c', key: 'demonstration', label: 'Demonstration' },
  { color: '#c9a227', key: 'production', label: 'Production Tooling' },
] satisfies { color: string; key: ActivityKey; label: string }[];

const formatMoney = (value: number): string => {
  if (value >= 1000) {
    return `$${(value / 1000).toFixed(1)}B`;
  }

  return `$${value.toFixed(0)}M`;
};

const ChartSection: FC<ChartSectionProps> = (props) => {
  const { children, className, description, title } = props;

  return (
    <section className={cn('rounded-lg border border-gray-200 bg-white p-4 shadow-sm', className)}>
      <div className="mb-3 flex flex-col gap-1">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {description && <p className="text-xs text-gray-500">{description}</p>}
      </div>
      {children}
    </section>
  );
};

const KpiStrip: FC<NavyCostTimeReportArtifactProps> = (props) => {
  const { report } = props;
  const kpis = [
    {
      ...report.kpis.totalCapital,
      label: 'Applicants',
      sublabel: `${report.meta.nations} nations - ${report.meta.techAreas} tech areas`,
      value: report.meta.applicants,
    },
    report.kpis.totalCapital,
    report.kpis.medianTimeToField,
    report.kpis.fastFieldable,
    report.kpis.medianCostPerCompany,
    report.kpis.highRiskSpend,
  ];

  return (
    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
      {kpis.map((kpi) => (
        <div
          className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm"
          key={kpi.label}
        >
          <div className="text-xs font-medium uppercase text-gray-500">{kpi.label}</div>
          <div
            className="mt-1 text-2xl font-semibold"
            style={{ color: kpi.color }}
          >
            {kpi.label.includes('Cost') || kpi.label.includes('Capital')
              ? formatMoney(kpi.value)
              : kpi.label.includes('Time')
                ? `${kpi.value.toFixed(0)} mo`
                : kpi.value.toFixed(0)}
          </div>
          <div className="mt-1 text-xs text-gray-500">{kpi.sublabel}</div>
          <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-gray-100">
            <div
              className="h-full rounded-full"
              style={{ backgroundColor: kpi.color, width: `${Math.min(100, Math.max(6, kpi.value))}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

const StageAnchors: FC<NavyCostTimeReportArtifactProps> = (props) => {
  const { report } = props;

  return (
    <ChartSection
      className="lg:col-span-2"
      description="Public DoW/DARPA/ONR calibrated stage anchors."
      title="TRL Stage Cost and Schedule Anchors"
    >
      <div className="overflow-auto">
        <table className="w-full min-w-[720px] text-left text-xs">
          <thead className="text-gray-500">
            <tr className="border-b">
              <th className="p-2">Transition</th>
              <th className="p-2">Stage</th>
              <th className="p-2">Cost Range</th>
              <th className="p-2">Duration</th>
            </tr>
          </thead>
          <tbody>
            {report.charts.stageAnchors.map((stage) => (
              <tr
                className="border-b last:border-b-0"
                key={stage.step}
              >
                <td className="p-2 font-semibold">{stage.step}</td>
                <td className="p-2">{stage.name}</td>
                <td className="p-2">
                  {formatMoney(stage.costP10)}-{formatMoney(stage.costP90)}
                  <span className="ml-2 text-gray-500">P50 {formatMoney(stage.costP50)}</span>
                </td>
                <td className="p-2">
                  {stage.durationMin}-{stage.durationMax} mo
                  <span className="ml-2 text-gray-500">P50 {stage.durationP50} mo</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-3 grid grid-cols-4 gap-1 text-center text-[10px] font-semibold uppercase text-gray-500 md:grid-cols-8">
        {report.charts.stageAnchors.map((stage) => (
          <div
            className="rounded bg-gray-100 px-2 py-1"
            key={`${stage.step}-strip`}
          >
            {stage.step}
          </div>
        ))}
      </div>
    </ChartSection>
  );
};

const SimpleBarChart: FC<{
  data: { color: string; label?: string; multiplier?: number; risk?: number; tech?: string }[];
  dataKey: 'multiplier' | 'risk';
  title: string;
}> = (props) => {
  const { data, dataKey, title } = props;

  return (
    <ChartSection title={title}>
      <div className="h-[360px]">
        <ResponsiveContainer
          height="100%"
          width="100%"
        >
          <BarChart
            data={data}
            layout="vertical"
            margin={{ bottom: 8, left: 24, right: 24, top: 8 }}
          >
            <CartesianGrid
              horizontal={false}
              stroke="#eef1f7"
            />
            <XAxis type="number" />
            <YAxis
              dataKey="tech"
              tick={{ fontSize: 11 }}
              type="category"
              width={120}
            />
            <Tooltip />
            <Bar
              dataKey={dataKey}
              radius={4}
            >
              {data.map((item) => (
                <Cell
                  fill={item.color}
                  key={item.tech}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartSection>
  );
};

const ActivityProfile: FC<NavyCostTimeReportArtifactProps> = (props) => {
  const { report } = props;

  return (
    <ChartSection
      description="Share of stage cost by activity across TRL transitions."
      title="Activity Cost Profile by TRL Stage"
    >
      <div className="h-[300px]">
        <ResponsiveContainer
          height="100%"
          width="100%"
        >
          <BarChart data={report.charts.activityProfile}>
            <CartesianGrid
              stroke="#eef1f7"
              vertical={false}
            />
            <XAxis
              dataKey="step"
              tick={{ fontSize: 10 }}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fontSize: 10 }}
            />
            <Tooltip />
            {ACTIVITY_KEYS.map((activity) => (
              <Bar
                dataKey={activity.key}
                fill={activity.color}
                key={activity.key}
                name={activity.label}
                stackId="activity"
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartSection>
  );
};

const Histograms: FC<NavyCostTimeReportArtifactProps> = (props) => {
  const { report } = props;

  return (
    <>
      <ChartSection title="Cohort Cost-to-Field Distribution">
        <div className="h-[260px]">
          <ResponsiveContainer
            height="100%"
            width="100%"
          >
            <BarChart data={report.charts.costHistogram.bins}>
              <CartesianGrid
                stroke="#eef1f7"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10 }}
              />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar
                dataKey="count"
                radius={4}
              >
                {report.charts.costHistogram.bins.map((bin) => (
                  <Cell
                    fill={bin.color}
                    key={bin.label}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartSection>
      <ChartSection title="Cohort Time-to-Field Distribution">
        <div className="h-[260px]">
          <ResponsiveContainer
            height="100%"
            width="100%"
          >
            <BarChart data={report.charts.timeHistogram.bins}>
              <CartesianGrid
                stroke="#eef1f7"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10 }}
              />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar
                dataKey="count"
                radius={4}
              >
                {report.charts.timeHistogram.bins.map((bin) => (
                  <Cell
                    fill={bin.color}
                    key={bin.label}
                  />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </ChartSection>
    </>
  );
};

const TechCostStack: FC<NavyCostTimeReportArtifactProps> = (props) => {
  const { report } = props;
  const data = report.charts.techCostStack.map((row) => ({
    ...row,
    label: row.tech.split(' ')[0] ?? row.tech,
  }));

  return (
    <ChartSection
      className="lg:col-span-2"
      description="Cohort cost stack by technology family and activity."
      title="Cost-to-Field by Tech Family"
    >
      <div className="h-[380px]">
        <ResponsiveContainer
          height="100%"
          width="100%"
        >
          <BarChart
            data={data}
            margin={{ bottom: 20, left: 8, right: 20, top: 8 }}
          >
            <CartesianGrid
              stroke="#eef1f7"
              vertical={false}
            />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 10 }}
            />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Legend />
            {ACTIVITY_KEYS.map((activity) => (
              <Bar
                dataKey={activity.key}
                fill={activity.color}
                key={activity.key}
                name={activity.label}
                stackId="techCost"
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartSection>
  );
};

const AtoByGroup: FC<NavyCostTimeReportArtifactProps> = (props) => {
  const { report } = props;

  return (
    <ChartSection
      description="Average compliance and ATO cost over 24 months."
      title="Compliance / ATO Cost Lens"
    >
      <div className="h-[260px]">
        <ResponsiveContainer
          height="100%"
          width="100%"
        >
          <BarChart data={report.charts.atoByGroup}>
            <CartesianGrid
              stroke="#eef1f7"
              vertical={false}
            />
            <XAxis
              dataKey="group"
              tick={{ fontSize: 10 }}
            />
            <YAxis tick={{ fontSize: 10 }} />
            <Tooltip />
            <Bar
              dataKey="value"
              radius={4}
            >
              {report.charts.atoByGroup.map((group) => (
                <Cell
                  fill={group.color}
                  key={group.group}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </ChartSection>
  );
};

const EfficientFrontier: FC<NavyCostTimeReportArtifactProps> = (props) => {
  const { report } = props;
  const frontierData = report.charts.efficientFrontier.filter((item) => item.costP50 > 0 && item.monthsP50 > 0);

  return (
    <ChartSection
      className="lg:col-span-2"
      description="Bottom-left is lower-cost and faster-to-field."
      title="Efficient Frontier - Cost vs Time"
    >
      <div className="h-[420px]">
        <ResponsiveContainer
          height="100%"
          width="100%"
        >
          <ScatterChart margin={{ bottom: 16, left: 8, right: 16, top: 8 }}>
            <CartesianGrid stroke="#eef1f7" />
            <XAxis
              domain={[0, 240]}
              dataKey="monthsP50"
              name="Months"
              tick={{ fontSize: 11 }}
              type="number"
            />
            <YAxis
              domain={[1, 5000]}
              dataKey="costP50"
              name="Cost"
              scale="log"
              tick={{ fontSize: 11 }}
              type="number"
            />
            <ZAxis
              dataKey="radius"
              range={[40, 180]}
            />
            <Tooltip />
            <Legend />
            {TIER_ORDER.map((tier) => (
              <Scatter
                data={frontierData.filter((item) => item.tier === tier)}
                fill={TIER_COLORS[tier]}
                key={tier}
                name={tier}
              />
            ))}
          </ScatterChart>
        </ResponsiveContainer>
      </div>
    </ChartSection>
  );
};

const DrilldownTable: FC<NavyCostTimeReportArtifactProps> = (props) => {
  const { report } = props;

  return (
    <ChartSection
      className="lg:col-span-3"
      title="Field-Now and Pilot Tier Cost / Time / Risk Drilldown"
    >
      <div className="max-h-[460px] overflow-auto">
        <table className="w-full min-w-[980px] text-left text-xs">
          <thead className="sticky top-0 bg-white text-gray-500">
            <tr className="border-b">
              <th className="p-2">#</th>
              <th className="p-2">Company</th>
              <th className="p-2">Tech</th>
              <th className="p-2">TRL/MRL</th>
              <th className="p-2">Months</th>
              <th className="p-2">P50 Cost</th>
              <th className="p-2">P10-P90</th>
              <th className="p-2">Risk</th>
              <th className="p-2">Tier</th>
            </tr>
          </thead>
          <tbody>
            {report.tables.costDrilldown.map((row) => (
              <tr
                className="border-b last:border-b-0"
                key={`${row.rank}-${row.company}`}
              >
                <td className="p-2 font-medium">{row.rank}</td>
                <td className="p-2 font-semibold text-gray-900">{row.company}</td>
                <td className="p-2">{row.tech}</td>
                <td className="p-2">
                  {row.trl}/{row.mrl}
                </td>
                <td className="p-2">{row.monthsP50.toFixed(0)}</td>
                <td className="p-2 font-semibold">{formatMoney(row.costP50)}</td>
                <td className="p-2 text-gray-600">
                  {formatMoney(row.costP10)}-{formatMoney(row.costP90)}
                </td>
                <td className="p-2">{row.scheduleRisk}%</td>
                <td className="p-2">{row.tier}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </ChartSection>
  );
};

export const NavyCostTimeReportArtifact: FC<NavyCostTimeReportArtifactProps> = (props) => {
  const { report } = props;

  return (
    <div className="flex flex-col gap-4 bg-[#f5f7fb] p-4">
      <div className="rounded bg-[#0b6b2d] px-3 py-1 text-center text-[11px] font-bold uppercase tracking-[0.18em] text-white">
        Unclassified // For Official Use Only - Illustrative Sample
      </div>
      <header className="border-b-[3px] border-[#0b2545] bg-[#f5f7fb] pb-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <h2 className="text-xl font-bold text-[#0b2545]">
              Department of the Navy - Rapid Capabilities Office - Cost & Schedule to Field
            </h2>
            <p className="mt-1 text-xs text-gray-600">
              Cohort: {report.meta.applicants} applicants - Cycle: {report.meta.cycle} - Methodology:{' '}
              {report.meta.methodologyLabel} - DoW alignment
            </p>
          </div>
          <div className="text-left text-[11px] text-gray-600 lg:text-right">
            <div className="font-semibold text-[#062726]">Assessment by Azymmetric - TYR Platform</div>
            <div>Doc ID: NRCO-26Q3-100-AZ-CT</div>
            <div>Sponsor: {report.meta.sponsor}</div>
          </div>
        </div>
      </header>
      <KpiStrip report={report} />
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <StageAnchors report={report} />
        <SimpleBarChart
          data={report.charts.techMultipliers}
          dataKey="multiplier"
          title="Tech-Family Cost Multiplier"
        />
        <ActivityProfile report={report} />
        <Histograms report={report} />
        <TechCostStack report={report} />
        <SimpleBarChart
          data={report.charts.scheduleRiskByTech}
          dataKey="risk"
          title="Schedule Risk by Tech"
        />
        <EfficientFrontier report={report} />
        <AtoByGroup report={report} />
        <DrilldownTable report={report} />
      </div>
    </div>
  );
};
